import classNames from 'classnames';
import { StaticImage } from 'gatsby-plugin-image';
import { useEffect, useRef, useState } from 'react';

import Button from '~components/Button/Button';
import { usePageContext } from '~components/PageDataProvider/PageDataProvider';
import PersonalizePlanReco from '~components/PersonalizePlanReco/PersonalizePlanReco';
import constants from '~globals/constants';
import { fireTrackEvent } from '~globals/tracking';
import { useAppDispatch, useAppSelector } from '~hooks/useRedux';
import ArrowIcon from '~images/inline/arrow-rebrand.inline.svg';
import { toggleZipFormModal } from '~redux/Actions/DisplayActions';
import { updateZipModalCta, updateZipModalHeading } from '~redux/Actions/GlobalActions';

import DetailsTab from './child-blocks/DetailsTab';
import MobileTile from './child-blocks/MobileTile';
import Tile from './child-blocks/Tile';

export type BenefitData = {
    body: string;
    bodyAlt: string;
    cta: string;
    filter: string;
    heading: string;
    icons: JSX.Element[];
    image: JSX.Element;
    name: string;
    page: number;
};

let observer: IntersectionObserver;

const benefits: BenefitData[] = [
    {
        name: '$0 Plan Premium',
        heading: '$0 monthly plan premiums',
        body: 'Medicare Advantage plans with $0 monthly premium coverage.',
        bodyAlt: 'Compare plans with $0 Premiums',
        cta: 'Compare plans with $0 premiums',
        filter: '2:drugs%7C1:0.0&category=ZeroMonthlyPremium',
        icons: [
            <StaticImage src="./icons/premium-default.png" alt="" key="0" className="lg:mb-2" />,
            <StaticImage src="./icons/premium-active.png" alt="" key="1" className="lg:mb-2" />,
        ],
        image: <StaticImage width={792} height={446} src="./images/img-premium.jpg" alt="" />,
        page: 1,
    },
    {
        name: 'Dental',
        heading: 'Dental coverage',
        body: 'Original Medicare does not cover dental services including checkups and cleanings. Medicare Advantage plans with dental coverage can provide coverage for dental care including access to a network of qualified dentists, discounts for covered care, procedures, and more.',
        bodyAlt: 'Compare plans with Dental Coverage',
        cta: 'Compare plans with dental coverage',
        filter: '2%3Adrugs%2Cdental',
        icons: [
            <StaticImage src="./icons/dental-default.png" alt="" key="0" className="lg:mb-2" />,
            <StaticImage src="./icons/dental-active.png" alt="" key="1" className="lg:mb-2" />,
        ],
        image: <StaticImage width={792} height={446} src="./images/img-dental.jpg" alt="" />,
        page: 2,
    },
    {
        name: 'Hearing',
        heading: 'Hearing coverage exams',
        body: 'Medicare Advantage plans with this additional benefit can provide coverage for hearing exams and may include coverage, discounts, or annual allowances for hearing aids, batteries, and more.',
        bodyAlt: 'Compare plans with hearing coverage',
        cta: 'Compare plans with hearing coverage',
        filter: '2%3Adrugs%2Chearing',
        icons: [
            <StaticImage src="./icons/hearing-default.png" alt="" key="0" className="lg:mb-2" />,
            <StaticImage src="./icons/hearing-active.png" alt="" key="1" className="lg:mb-2" />,
        ],
        image: <StaticImage width={792} height={446} src="./images/img-hearing.jpg" alt="" />,
        page: 3,
    },
    {
        name: 'Vision',
        heading: 'Vision coverage',
        body: 'Medicare Advantage plans with this additional benefit can provide coverage for routine eye exams and screenings. They may also help cover the cost of prescription eyewear such as lenses, frames or contacts.',
        bodyAlt: 'Compare plans with vision coverage',
        cta: 'Compare plans with vision coverage',
        filter: '2%3Adrugs%2Cvision',
        icons: [
            <StaticImage src="./icons/vision-default.png" alt="" key="0" className="lg:mb-2" />,
            <StaticImage src="./icons/vision-active.png" alt="" key="1" className="lg:mb-2" />,
        ],
        image: <StaticImage width={792} height={446} src="./images/img-vision.jpg" alt="" />,
        page: 4,
    },
    {
        name: 'Fitness',
        heading: 'Gym and fitness benefits',
        body: 'Medicare Advantage plans with this additional benefit may offer fitness classes, gym memberships or other fitness programs like online classes and video workouts.',
        bodyAlt: 'Compare plans with gym memberships',
        cta: 'Compare plans with fitness benefits',
        filter: '2%3Adrugs%2CfitnessBenefits',
        icons: [
            <StaticImage src="./icons/fitness-default.png" alt="" key="0" className="mb-0" />,
            <StaticImage src="./icons/fitness-active.png" alt="" key="1" className="mb-0" />,
        ],
        image: <StaticImage width={792} height={446} src="./images/img-fitness.jpg" alt="" />,
        page: 5,
    },
    {
        name: 'Telehealth',
        heading: 'Convenient virtual visits',
        body: 'Connect with a doctor over the phone or through your computer to get receive care for services including therapy sessions and regular check-ups, all from the comfort of your own home.',
        bodyAlt: 'Compare plans with telehealth coverage',
        cta: 'Compare plans with telehealth coverage',
        filter: '2%3Adrugs,telehealthServices',
        icons: [
            <StaticImage src="./icons/telehealth-default.png" alt="" key="0" className="lg:mb-2" />,
            <StaticImage src="./icons/telehealth-active.png" alt="" key="1" className="lg:mb-2" />,
        ],
        image: <StaticImage width={792} height={446} src="./images/img-telehealth.jpg" alt="" />,
        page: 6,
    },
    {
        name: '$0 Medical Deductible',
        heading: 'No upfront costs before your plan begins to pay',
        body: 'A medical deductible is the amount you pay for covered services before your plan begins to pay. With these zero-dollar deductible plans, your plan starts paying immediately with no upfront out-of-pocket costs for covered medical care.',
        bodyAlt: 'Compare plans with $0 medical deductibles',
        cta: 'Compare plans',
        filter: '2%3Adrugs',
        icons: [
            <StaticImage src="./icons/deductible-default.png" alt="" key="0" className="lg:mb-2" />,
            <StaticImage src="./icons/deductible-active.png" alt="" key="1" className="lg:mb-2" />,
        ],
        image: <StaticImage width={792} height={446} src="./images/img-deductible.jpg" alt="" />,
        page: 7,
    },
    {
        name: '$0 Doctor Copays',
        heading: "Zero copay doctor's office visits",
        body: 'Medicare Advantage plans with this benefit allow you to visit your primary care doctor without a copay.',
        bodyAlt: 'Compare plans with $0 doctor copays',
        cta: 'Compare plans',
        filter: '2%3Adrugs',
        icons: [
            <StaticImage src="./icons/copay-default.png" alt="" key="0" className="lg:mb-2" />,
            <StaticImage src="./icons/copay-active.png" alt="" key="1" className="lg:mb-2" />,
        ],
        image: <StaticImage width={792} height={446} src="./images/img-copay.jpg" alt="" />,
        page: 8,
    },
];

const BenefitsExplorer = ({
    backgroundsurface = 'bg-surface-100',
    heading = 'Explore select {{CURRENT_YEAR}} Medicare Advantage Plan coverage and benefits in {{COUNTY_NAME}} county, {{STATE_ABBR}}',
}: {
    backgroundsurface: SupportedBackgroundColor;
    heading: string;
}) => {
    const [selectedBenefit, setSelectedBenefit] = useState(benefits[0]);
    const [formattedLocation, setFormattedLocation] = useState('');

    const mobileContainer = useRef(null as HTMLDivElement | null);

    const dispatch = useAppDispatch();

    const { countyName, stateAbbr } = useAppSelector((state) => state.global.geoLocation);

    const currentYear = usePageContext().annualCostData.customProperties.CURRENT_YEAR.value;

    const isExcludedCounty =
        stateAbbr && constants.EXCLUDED_COUNTIES.includes(`${countyName}, ${stateAbbr}`);

    const parsedHeading = heading
        .replaceAll('{{CURRENT_YEAR}}', currentYear)
        .replaceAll(
            '{{COUNTY_NAME}}',
            countyName
                ? countyName
                      .toLowerCase()
                      .split(' ')
                      .map((s) => s.charAt(0).toLocaleUpperCase() + s.substring(1))
                      .join(' ')
                : 'your'
        )
        .replaceAll('{{STATE_ABBR}}', stateAbbr ? stateAbbr.toUpperCase() : 'US')
        .replaceAll('{{FORMATTED_LOCATION}}', formattedLocation);

    const triggerZipModal = () => {
        fireTrackEvent({ page: { action: 'Feature Benefits: edit location' } });
        dispatch(
            updateZipModalHeading(
                'Enter your zip code to compare the benefits and coverage available on plans in your area.'
            )
        );
        dispatch(updateZipModalCta('See plans in your area'));
        dispatch(toggleZipFormModal(true));
    };

    const goToNext = () => {
        const nextPageNumber = selectedBenefit.page + 1;
        const next = benefits.find((benefit) => benefit.page === nextPageNumber);
        if (next) {
            setSelectedBenefit(next);
            scrollToPage(nextPageNumber);
        }
    };

    const goToPrevious = () => {
        const previousPageNumber = selectedBenefit.page - 1;
        const previous = benefits.find((benefit) => benefit.page === previousPageNumber);
        if (previous) {
            setSelectedBenefit(previous);
            scrollToPage(previousPageNumber);
        }
    };

    const scrollToPage = (pageNumber: number) => {
        if (!mobileContainer.current?.firstElementChild) return;
        const gapWidth = 16;
        const cardWidth = mobileContainer.current.firstElementChild.clientWidth;
        mobileContainer.current.scrollLeft = (cardWidth + gapWidth) * (pageNumber - 1);
    };

    const handleScroll = (entries: Array<IntersectionObserverEntry>) => {
        entries.forEach((entry) => {
            if (entry.intersectionRatio > 0.5) {
                const newSelectedBenefit = benefits.find(
                    (benefit) =>
                        benefit.page.toString() === (entry.target as HTMLDivElement).dataset.page
                );
                newSelectedBenefit && setSelectedBenefit(newSelectedBenefit);
            }
        });
    };

    const handleTileSelect = (benefit: BenefitData) => {
        fireTrackEvent({ page: { action: `Feature Benefits: ${benefit.name}` } });
        setSelectedBenefit(benefit);
    };

    useEffect(() => {
        observer = new IntersectionObserver(handleScroll, {
            root: mobileContainer.current,
            threshold: [0.25, 0.5, 0.75],
        });
    }, []);

    useEffect(() => {
        if (!(countyName && stateAbbr)) {
            setFormattedLocation('');
        } else {
            const countyPascalCase = countyName
                .split(' ')
                .map((word) => {
                    return word[0].toLocaleUpperCase() + word.slice(1).toLocaleLowerCase();
                })
                .join(' ');
            setFormattedLocation(`${countyPascalCase} county, ${stateAbbr}`);
        }
    }, [countyName, stateAbbr]);

    return !isExcludedCounty ? (
        <section
            className={classNames('w-full', 'px-4 md:px-6', 'py-10 lg:pb-36', backgroundsurface)}
        >
            <div className="max-w-screen-xl mx-auto">
                <h2
                    className={classNames(
                        'font-poppins font-bold',
                        'text-18 leading-28',
                        'md:text-32 md:leading-40',
                        'lg:text-40 lg:leading-56',
                        'text-center md:text-left',
                        'mb-8 md:mb-10 py-4'
                    )}
                >
                    {parsedHeading}
                    <span className="hidden md:inline">
                        <Button
                            styletype="link"
                            className="underline text-16 font-normal ml-4"
                            onClick={triggerZipModal}
                        >
                            <span className="text-primary">Change Location</span>
                        </Button>
                    </span>
                </h2>
                <div
                    ref={mobileContainer}
                    className={classNames(
                        'flex md:hidden gap-4',
                        'h-80 pt-4 pb-6',
                        'rounded-lg shadow-md bg-white',
                        'snap-mandatory snap-x overflow-x-scroll'
                    )}
                >
                    {benefits.map((benefit) => (
                        <MobileTile
                            data={benefit}
                            key={benefit.name}
                            ref={(el) => {
                                el && observer?.observe(el);
                            }}
                        />
                    ))}
                </div>
                <div className="flex md:hidden justify-center gap-6 mt-8">
                    <button aria-label="previous benefit" onClick={goToPrevious}>
                        <ArrowIcon
                            className={classNames(
                                'transform rotate-180',
                                selectedBenefit.page > 1 ? 'text-link-visited' : 'text-[#cccccc]'
                            )}
                        />
                    </button>
                    <span className="text-18 font-semibold text-link-visited font-poppins">
                        {selectedBenefit.page} of 8
                    </span>
                    <button aria-label="next benefit" onClick={goToNext}>
                        <ArrowIcon
                            className={classNames(
                                selectedBenefit.page < 8 ? 'text-link-visited' : 'text-[#cccccc]'
                            )}
                        />
                    </button>
                </div>
                <div className="hidden md:flex justify-between gap-x-6">
                    <div
                        className={classNames(
                            'flex flex-col',
                            'benefits:flex-row flex-wrap benefits:w-1/3',
                            'gap-3 benefits:gap-y-6 benefits:gap-x-8 h-min'
                        )}
                    >
                        {benefits.map((benefit) => (
                            <Tile
                                key={benefit.name}
                                benefit={benefit}
                                isSelected={benefit.name === selectedBenefit.name}
                                setSelected={handleTileSelect}
                            />
                        ))}
                    </div>
                    <DetailsTab benefit={selectedBenefit} />
                </div>
            </div>
        </section>
    ) : (
        <PersonalizePlanReco backgroundsurface={backgroundsurface} />
    );
};

export default BenefitsExplorer;
